import React, { useState, useEffect, useCallback } from "react";
import { Button } from "./ui/button";
import { navLinks, companyInfo } from "../data/mock";
import {
  Menu,
  X,
  Phone,
  Recycle,
  ChevronRight,
  MessageCircle,
} from "lucide-react";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setMobileOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const handleNavClick = useCallback(
    (href) => {
      setMobileOpen(false);
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) {
          const offset = 80;
          const top = el.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top, behavior: "smooth" });
        }
      }, 100);
    },
    []
  );

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || mobileOpen
            ? "bg-white/95 backdrop-blur-xl shadow-lg shadow-emerald-900/5 py-2.5 sm:py-3"
            : "bg-transparent py-4 sm:py-5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          {/* Logo */}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setMobileOpen(false);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex items-center gap-2 sm:gap-2.5 group flex-shrink-0"
          >
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-emerald-700 flex items-center justify-center group-hover:bg-emerald-600 transition-colors duration-300">
              <Recycle className="w-4.5 h-4.5 sm:w-5 sm:h-5 text-white" />
            </div>
            <div className="flex flex-col">
              <span
                className={`text-base sm:text-lg font-bold leading-tight tracking-tight transition-colors duration-300 ${
                  scrolled || mobileOpen ? "text-slate-900" : "text-white"
                }`}
              >
                {companyInfo.name}
              </span>
              <span
                className={`text-[9px] sm:text-[10px] uppercase tracking-[0.15em] sm:tracking-[0.2em] font-medium transition-colors duration-300 ${
                  scrolled || mobileOpen
                    ? "text-emerald-700"
                    : "text-emerald-300"
                }`}
              >
                Authorized Dealer
              </span>
            </div>
          </a>

          {/* Desktop Nav - visible on lg+ */}
          <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link.href)}
                className={`px-2.5 xl:px-3.5 py-2 text-[13px] xl:text-sm font-medium rounded-lg transition-colors duration-200 whitespace-nowrap ${
                  scrolled
                    ? "text-slate-600 hover:text-emerald-700 hover:bg-emerald-50"
                    : "text-white/80 hover:text-white hover:bg-white/10"
                }`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Desktop CTA - visible on lg+ */}
          <div className="hidden lg:flex items-center gap-2 xl:gap-3 flex-shrink-0">
            <a
              href={`tel:${companyInfo.phone}`}
              className="flex items-center gap-2"
            >
              <Phone
                className={`w-4 h-4 ${
                  scrolled ? "text-emerald-700" : "text-emerald-300"
                }`}
              />
              <span
                className={`text-sm font-semibold whitespace-nowrap ${
                  scrolled ? "text-slate-700" : "text-white"
                }`}
              >
                {companyInfo.phone}
              </span>
            </a>
            <Button
              className="bg-emerald-700 hover:bg-emerald-600 text-white rounded-lg px-4 xl:px-5 shadow-lg shadow-emerald-700/20 text-sm"
              onClick={() => handleNavClick("#contact")}
            >
              Get Quote
            </Button>
          </div>

          {/* Tablet CTA - visible on md only */}
          <div className="hidden md:flex lg:hidden items-center gap-2 flex-shrink-0">
            <a
              href={`tel:${companyInfo.phone}`}
              className={`p-2 rounded-lg transition-colors ${
                scrolled || mobileOpen
                  ? "text-emerald-700 hover:bg-emerald-50"
                  : "text-emerald-300 hover:bg-white/10"
              }`}
            >
              <Phone className="w-5 h-5" />
            </a>
            <Button
              className="bg-emerald-700 hover:bg-emerald-600 text-white rounded-lg px-4 shadow-lg shadow-emerald-700/20 text-sm"
              onClick={() => handleNavClick("#contact")}
            >
              Get Quote
            </Button>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`p-2 rounded-lg transition-colors ml-1 ${
                scrolled || mobileOpen
                  ? "text-slate-700 hover:bg-slate-100"
                  : "text-white hover:bg-white/10"
              }`}
              aria-label="Toggle menu"
            >
              {mobileOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </button>
          </div>

          {/* Mobile Toggle - visible on sm/xs only */}
          <div className="flex md:hidden items-center gap-1.5">
            <a
              href={`https://wa.me/${companyInfo.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className={`p-2 rounded-lg transition-colors ${
                scrolled || mobileOpen
                  ? "text-[#25D366] hover:bg-green-50"
                  : "text-[#25D366] hover:bg-white/10"
              }`}
              aria-label="WhatsApp"
            >
              <MessageCircle className="w-5 h-5" />
            </a>
            <a
              href={`tel:${companyInfo.phone}`}
              className={`p-2 rounded-lg transition-colors ${
                scrolled || mobileOpen
                  ? "text-emerald-700 hover:bg-emerald-50"
                  : "text-emerald-300 hover:bg-white/10"
              }`}
              aria-label="Call us"
            >
              <Phone className="w-5 h-5" />
            </a>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`p-2 rounded-lg transition-colors ${
                scrolled || mobileOpen
                  ? "text-slate-700 hover:bg-slate-100"
                  : "text-white hover:bg-white/10"
              }`}
              aria-label="Toggle menu"
            >
              {mobileOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile/Tablet Full-screen Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${
          mobileOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/20 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        />

        {/* Menu Panel */}
        <div
          className={`absolute top-[56px] sm:top-[60px] left-0 right-0 bg-white shadow-2xl transition-all duration-300 overflow-y-auto ${
            mobileOpen
              ? "translate-y-0 opacity-100"
              : "-translate-y-4 opacity-0"
          }`}
          style={{ maxHeight: "calc(100vh - 60px)" }}
        >
          <div className="px-4 sm:px-6 py-4 sm:py-6">
            {/* Nav Links */}
            <nav className="space-y-1">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link.href)}
                  className="w-full flex items-center justify-between px-4 py-3.5 text-slate-700 hover:text-emerald-700 hover:bg-emerald-50 rounded-xl text-[15px] font-medium transition-colors group"
                >
                  <span>{link.label}</span>
                  <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-500 transition-colors" />
                </button>
              ))}
            </nav>

            {/* Divider */}
            <div className="my-4 sm:my-5 border-t border-slate-100" />

            {/* Contact Info */}
            <div className="space-y-3 mb-5">
              <a
                href={`tel:${companyInfo.phone}`}
                className="flex items-center gap-3 px-4 py-3 bg-stone-50 rounded-xl hover:bg-emerald-50 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-4.5 h-4.5 text-emerald-700" />
                </div>
                <div>
                  <div className="text-[11px] text-slate-400 font-medium uppercase tracking-wider">
                    Call Us
                  </div>
                  <div className="text-sm font-semibold text-slate-800">
                    {companyInfo.phone}
                  </div>
                </div>
              </a>

              <a
                href={`https://wa.me/${companyInfo.whatsapp}?text=Hi%20AM%20Scrap%20Dealers%2C%20I%20would%20like%20to%20enquire%20about%20scrap%20services.`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-4 py-3 bg-stone-50 rounded-xl hover:bg-green-50 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-[#25D366]/15 flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-4.5 h-4.5 text-[#25D366]" />
                </div>
                <div>
                  <div className="text-[11px] text-slate-400 font-medium uppercase tracking-wider">
                    WhatsApp
                  </div>
                  <div className="text-sm font-semibold text-slate-800">
                    Chat Instantly
                  </div>
                </div>
              </a>
            </div>

            {/* CTAs */}
            <div className="grid grid-cols-2 gap-3">
              <Button
                className="bg-emerald-700 hover:bg-emerald-600 text-white rounded-xl py-5 text-sm font-semibold shadow-lg shadow-emerald-700/15 w-full"
                onClick={() => handleNavClick("#contact")}
              >
                Get Quote
              </Button>
              <Button
                variant="outline"
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 rounded-xl py-5 text-sm font-semibold w-full"
                onClick={() => handleNavClick("#how-it-works")}
              >
                Schedule Pickup
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
